import React, { useMemo, useState } from 'react'
import { FormProvider, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form'
import { CloseButton } from 'react-bootstrap'

import { AllocationUserTagDropdownItemOption } from '../../MeetingSummary/ConfigureAssignment/AllocationUserTagDropdownItemOption'

import { SelectDropdown, SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { TagElement } from '../../../../../components/Tag/Tag'
import { Modal } from '../../../../../components/Modal/Modal'

import { isLiveSession } from '../../../../../common/session.util'
import { usePrivateMeetingsEnabled } from '../../../../../common/usePrivateMeetingsEnabled'

import { useAddAllocationContextUsersMutation } from '../../../../../store/media/media'
import { selectRoomSession, selectRoomSessionCreatedBy } from '../../../../../store/meetingSummary/roomSession'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { useGetUsersQuery } from '../../../../../store/user/user'
import { useAppSelector, useAppDispatch } from '../../../../../store/hooks'
import { showToast } from '../../../../../store/meetingSummary/display'

import { UserAllocationContextUser } from '../../../../../models/Allocation'
import { User } from '../../../../../models/User'

import { TrackEvent, useTracking } from '../../common/tracking'
import { getUserDisplayName } from '../../common/aifeatures/utils'

import { mapUserToSelectOption } from './utils'

export interface AddParticipantModalProps {
  show: boolean
  userAllocationContextUsers: UserAllocationContextUser[]
  onCancel: () => void
  onSuccess: () => void
  polling?: boolean
}

interface AddParticipantModalFormValues {
  selectedUsers: User[]
}

export const AddParticipantModal: React.FC<AddParticipantModalProps> = ({ show, onCancel, onSuccess, userAllocationContextUsers, polling }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id
  const roomSession = useAppSelector(selectRoomSession)
  const roomSessionCreatedBy = useAppSelector(selectRoomSessionCreatedBy)
  const { trackingClient } = useTracking()

  const { privateMeetingsEnabled } = usePrivateMeetingsEnabled()

  const form = useForm<AddParticipantModalFormValues>({ defaultValues: { selectedUsers: [] } })
  const { control } = form

  const selectedUsers = useWatch({ control, name: 'selectedUsers' })

  const [triggerAddAllocationContextUsers, { isLoading: addingAllocationContextUser }] = useAddAllocationContextUsersMutation()

  const onSubmit = async (formData: AddParticipantModalFormValues) => {
    const { selectedUsers } = formData

    if (!roomSession || !selectedUsers.length) {
      return
    }

    const { roomId, id: sessionId } = roomSession
    const payload = selectedUsers.map((user) => ({
      userId: user.id,
      name: user.name || getUserDisplayName(user),
      email: user.email,
      phoneNumber: user.phoneNumber,
    }))

    try {
      await triggerAddAllocationContextUsers({ accountId, roomId, sessionId, body: payload }).unwrap()
      dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'New participants added' }))
      const source = isLiveSession(roomSession) ? 'live' : 'prep'
      for (const user of selectedUsers) {
        const participantType = user.type === 'managed' ? 'teammate' : 'client'
        trackingClient.track({ eventName: TrackEvent.PARTICIPANT_ADDED, eventDetails: { userId: user.id, type: participantType, source, roomId, sessionId } })
      }
      onSuccess()
    } catch (e) {
      console.error(e)
    }
  }

  if (!roomSession || !roomSessionCreatedBy) {
    return null
  }

  const currentUserIsMeetingOwner = roomSessionCreatedBy?.id === currentUserId
  const meetingIsPrivate = privateMeetingsEnabled && roomSession.isPrivate

  return (
    <Modal
      show={show}
      onHide={onCancel}
      header={<h4>{!meetingIsPrivate || currentUserIsMeetingOwner ? 'Add participant' : 'Add Client'}</h4>}
      body={
        <FormProvider {...form}>
          <AddParticipantModalBody
            currentUserHasFullAccessToMeeting={!meetingIsPrivate || currentUserIsMeetingOwner}
            meetingIsPrivate={meetingIsPrivate}
            userAllocationContextUsers={userAllocationContextUsers}
          />
        </FormProvider>
      }
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={form.handleSubmit(onSubmit)}
      footerPrimaryActionTitle='Add'
      footerPrimaryActionLoading={addingAllocationContextUser || polling}
      footerPrimaryActionDisabled={!selectedUsers.length}
      onFooterSecondaryAction={onCancel}
      centered={true}
    />
  )
}

interface AddParticipantModalBodyProps {
  currentUserHasFullAccessToMeeting: boolean
  meetingIsPrivate: boolean
  userAllocationContextUsers: UserAllocationContextUser[]
}

const AddParticipantModalBody: React.FC<AddParticipantModalBodyProps> = ({
  currentUserHasFullAccessToMeeting,
  meetingIsPrivate,
  userAllocationContextUsers,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const { control } = useFormContext<AddParticipantModalFormValues>()

  const { fields: selectedUsers, append, remove } = useFieldArray({ control, name: 'selectedUsers' })

  const [searchInput, setSearchInput] = useState<string>('')

  const { data: usersResponse } = useGetUsersQuery({
    accountId,
    name: searchInput,
    identityTypes: currentUserHasFullAccessToMeeting ? ['external', 'idp'] : ['external'],
    pageRequest: { page: 0, size: 50 },
  })

  const userOptions = useMemo(
    () =>
      usersResponse?.data
        .filter((user) => !userAllocationContextUsers.find((allocationUser) => allocationUser.user.id === user.id))
        .map(mapUserToSelectOption) ?? [],
    [userAllocationContextUsers, usersResponse],
  )

  const selectedUsersContainManagedUser = useMemo(() => selectedUsers.some((user) => user.type === 'managed'), [selectedUsers])

  const handleOptionSelected = (selectedOption: SelectOption) => {
    const selectedUser = usersResponse?.data.find((user) => user.id === selectedOption.id)
    if (!selectedUser) {
      return
    }

    append(selectedUser)
  }

  const handleSearchInputChange = async (newSearchInput: string) => {
    setSearchInput(newSearchInput)
  }

  const handleRemoveUser = (user: User) => {
    const index = selectedUsers.findIndex((selectedUser) => selectedUser.id === user.id)
    if (index === -1) {
      return
    }

    remove(index)
  }

  if (currentUserHasFullAccessToMeeting) {
    return (
      <div className='d-flex flex-column gap-4'>
        <span className='paragraph-medium'>Add clients to generate personalized meeting prep materials. </span>
        <span className='paragraph-medium'>
          You can also add teammates to ensure the right contacts are associated with this meeting or let Zocks automatically detect them during the meeting.
        </span>
        <SelectDropdown
          className='py-2 zocks-px-0.75 zocks-gap-3'
          options={userOptions}
          initialSelectedOption={null}
          onOptionSelected={handleOptionSelected}
          onSearchInputChange={handleSearchInputChange}
          placeholder='Select participant'
          clearDropdownOnOptionSelected={true}
          DropDownItemOptionComponent={AllocationUserTagDropdownItemOption}
          searchable
        />
        {selectedUsers.length ? (
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            {selectedUsers.map((user) => (
              <TagElement key={user.id}>
                {getUserDisplayName(user)} <CloseButton className='user-close-btn' onClick={() => handleRemoveUser(user)} />
              </TagElement>
            ))}
          </div>
        ) : null}
        {meetingIsPrivate && selectedUsersContainManagedUser ? (
          <div className='d-flex align-items-center zocks-gap-0.75 zocks-text-warning-text zocks-bg-color-warning zocks-border-color-warning-dark rounded-3 zocks-px-0.75 py-2'>
            <i className='bi bi-exclamation-triangle fs-6 ' />
            <span className='paragraph-small'>This meeting is private. Adding other team members will share it with them.</span>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='paragraph-medium'>Add clients to generate personalized meeting prep materials.</div>
      <SelectDropdown
        className='py-2 zocks-px-0.75 zocks-gap-3'
        options={userOptions}
        initialSelectedOption={null}
        onOptionSelected={handleOptionSelected}
        onSearchInputChange={handleSearchInputChange}
        placeholder='Select Contact'
        clearDropdownOnOptionSelected={true}
        DropDownItemOptionComponent={AllocationUserTagDropdownItemOption}
        searchable
      />
      {selectedUsers.length ? (
        <div className='d-flex align-items-center gap-2 flex-wrap'>
          {selectedUsers.map((user) => (
            <TagElement key={user.id}>
              {getUserDisplayName(user)} <CloseButton className='user-close-btn' onClick={() => handleRemoveUser(user)} />
            </TagElement>
          ))}
        </div>
      ) : null}
      <div className='paragraph-medium'>
        <strong>Want to add Teammates?</strong> You don’t need to. They will be automatically detected and added during the meeting.
      </div>
    </div>
  )
}
